/**
 * `{{path}}` / `{{path|filter}}` interpolation — RULES-ENGINE.md.
 *
 * Roots: answers · projection · archetype · settings (public subset) · plan · session.
 * Filters: num · pct · upper · lower.
 * A missing path resolves to "" and is reported (console warning in dev,
 * `template_missing` event in prod) through `onMissing`.
 */

import { formatNum } from './slider.ts';

export interface TemplateRoots {
  answers?: unknown;
  projection?: unknown;
  archetype?: unknown;
  settings?: unknown;
  plan?: unknown;
  session?: unknown;
}

export type MissingHandler = (path: string) => void;

const ROOTS = ['answers', 'projection', 'archetype', 'settings', 'plan', 'session'];

const PATTERN = /\{\{\s*([a-zA-Z0-9_.[\]]+)\s*(?:\|\s*([a-zA-Z]+)\s*)?\}\}/g;

function walk(root: unknown, path: string[]): unknown {
  let node: unknown = root;
  for (const seg of path) {
    if (node == null) return undefined;
    node = (node as Record<string, unknown>)[seg];
  }
  return node;
}

/** Answers are objects; a bare reference means its display value. */
function unwrap(v: unknown): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    if ('label' in o && typeof o.label === 'string') return o.label;
    if ('value' in o) return o.value;
  }
  return v;
}

function numify(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number(v.replace(/[^0-9.-]/g, ''));
  if (v && typeof v === 'object' && 'value' in (v as Record<string, unknown>)) {
    return numify((v as Record<string, unknown>).value);
  }
  return NaN;
}

const FILTERS: Record<string, (v: unknown) => string> = {
  num: (v) => formatNum(numify(v)),
  pct: (v) => {
    const n = numify(v);
    return Number.isFinite(n) ? `${Math.round(n <= 1 ? n * 100 : n)}%` : '';
  },
  upper: (v) => String(unwrap(v) ?? '').toUpperCase(),
  lower: (v) => String(unwrap(v) ?? '').toLowerCase(),
};

/** `{{highlight}}` is the title highlight slot, not a template path. */
export const isHighlightSlot = (path: string) => path === 'highlight';

export function resolvePath(roots: TemplateRoots, path: string): unknown {
  const parts = path.split('.');
  if (!ROOTS.includes(parts[0])) return undefined;
  return walk(roots[parts[0] as keyof TemplateRoots], parts.slice(1));
}

export function render(input: string, roots: TemplateRoots, onMissing?: MissingHandler): string {
  if (!input || input.indexOf('{{') === -1) return input ?? '';
  return input.replace(PATTERN, (whole, path: string, filter?: string) => {
    if (isHighlightSlot(path)) return whole;
    const raw = resolvePath(roots, path);
    if (raw === undefined || raw === null || raw === '') {
      onMissing?.(path);
      return '';
    }
    if (filter) {
      const fn = FILTERS[filter];
      if (!fn) {
        onMissing?.(`${path}|${filter}`);
        return '';
      }
      return fn(raw);
    }
    const out = unwrap(raw);
    return typeof out === 'number' ? String(out) : String(out ?? '');
  });
}

/** Renders every string in a content block. */
export function renderDeep<T>(node: T, roots: TemplateRoots, onMissing?: MissingHandler): T {
  if (typeof node === 'string') return render(node, roots, onMissing) as unknown as T;
  if (Array.isArray(node)) return node.map((n) => renderDeep(n, roots, onMissing)) as unknown as T;
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) out[k] = renderDeep(v, roots, onMissing);
    return out as unknown as T;
  }
  return node;
}

/** `[PH]` markers never render; the validator is what blocks them from production. */
export const stripPlaceholder = (s: string): string => (typeof s === 'string' ? s.replace(/\[PH\]\s*/g, '') : s);

/** stripPlaceholder over a whole content block. */
export function stripPlaceholderDeep<T>(node: T): T {
  if (typeof node === 'string') return stripPlaceholder(node) as unknown as T;
  if (Array.isArray(node)) return node.map(stripPlaceholderDeep) as unknown as T;
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) out[k] = stripPlaceholderDeep(v);
    return out as unknown as T;
  }
  return node;
}
